import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, InputAdornment, Menu, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import {
  AddRounded as AddIcon,
  SearchRounded as SearchIcon,
  MoreHorizRounded as MoreIcon,
  LocalShippingOutlined as TruckIcon,
} from '@mui/icons-material';
import VehicleService, { Vehicle } from '../../api/services/vehicle.service';
import DocumentService, { RosterRow } from '../../api/services/document.service';
import { useAuthStore } from '../../store/authStore';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import DataTable, { Column } from '../../components/ui/DataTable';
import ToneChip from '../../components/ui/ToneChip';
import { EmptyState } from '../../components/ui/States';
import FleetPanel, { FLEET_FILTERS, FleetFilter } from './FleetPanel';
import VehicleDialog from './VehicleDialog';
import ImportVehiclesDialog from './ImportVehiclesDialog';
import { toneForObligation, labelForObligation } from '../../components/files/vaultStatus';
import { status as statusTokens } from '../../theme';

// The fleet: what trucks exist, who they belong to, and what state each one
// is in.
//
// This is the asset record, and it is deliberately separate from the
// vault's Vehicles roster. That screen answers "whose paperwork is late";
// this one answers "what do we own, and is it on the road". They link to
// each other rather than each growing half of the other's job — a truck's
// row here opens its file drawer, and the compliance column is read from
// the same roster the vault uses so the two can never disagree.


const FleetList: React.FC = () => {
  const navigate = useNavigate();
  // ?show= and ?add= let a truck's own page drive this one: its panel is
  // the same panel, and its filters and Add land here.
  const [params, setParams] = useSearchParams();
  const { canArea, areaScoped } = useAuthStore();
  // Retiring is AO/CA only on the server. Hiding it for a BC is a courtesy,
  // not the gate.
  // Retiring and importing reach past one service area: fleet manage, company-wide.
  const canRetire = canArea('fleet', 'manage') && !areaScoped('fleet');
  // A fleet file carries financing and tax columns: AO/CA only, as on the server.
  const canImport = canRetire;

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [compliance, setCompliance] = useState<Record<string, RosterRow>>({});
  const shown = params.get('show') as FleetFilter | null;
  const [filter, setFilter] = useState<FleetFilter>(shown && FLEET_FILTERS.includes(shown) ? shown : 'all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuVehicle, setMenuVehicle] = useState<Vehicle | null>(null);
  const [retiring, setRetiring] = useState<Vehicle | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Retired trucks are always fetched and filtered here: the counts in
      // the panel have to be right before you click the filter that shows
      // them.
      const [fleet, roster] = await Promise.all([
        VehicleService.list({ includeRetired: true }),
        DocumentService.getRoster('vehicle', true).catch(() => [] as RosterRow[]),
      ]);
      setVehicles(fleet);
      setCompliance(Object.fromEntries(roster.map((row) => [row.ownerId, row])));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the fleet');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (shown && FLEET_FILTERS.includes(shown)) setFilter(shown);
    if (params.get('add')) { setEditing(null); setDialogOpen(true); }
    if (shown || params.get('add')) setParams({}, { replace: true });
  }, [shown, params, setParams]);

  const counts = useMemo(() => ({
    active: vehicles.filter((v) => v.status === 'active').length,
    maintenance: vehicles.filter((v) => v.status === 'maintenance').length,
    retired: vehicles.filter((v) => v.status === 'retired').length,
    all: vehicles.length,
  }), [vehicles]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return vehicles
      .filter((v) => (filter === 'all' ? true : v.status === filter))
      .filter((v) => !term || [v.unitNumber, v.vin, v.make, v.model, v.licensePlate]
        .some((field) => (field || '').toLowerCase().includes(term)))
      // Retired trucks sit at the bottom of All vehicles, greyed — still
      // findable, never mixed in with the ones on the road.
      .sort((a, b) => Number(a.status === 'retired') - Number(b.status === 'retired')
        || a.unitNumber.localeCompare(b.unitNumber, undefined, { numeric: true }));
  }, [vehicles, filter, search]);

  const act = async (fn: () => Promise<Vehicle>) => {
    setBusy(true);
    try {
      await fn();
      await load();
      setRetiring(null);
      setMenuVehicle(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<Vehicle>[] = [
    {
      key: 'unit',
      header: 'Unit',
      width: 190,
      render: (v) => (
        <Stack direction="row" gap={1.25} alignItems="center">
          <TruckIcon fontSize="small" sx={{ color: v.status === 'retired' ? 'text.disabled' : 'primary.main' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>{v.unitNumber}</Typography>
            {(v.year || v.make || v.model || v.bodyType) && (
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {[[v.year, v.make, v.model].filter(Boolean).join(' '), v.bodyType].filter(Boolean).join(' · ')}
              </Typography>
            )}
          </Box>
        </Stack>
      ),
    },
    {
      key: 'plate',
      header: 'Plate',
      hide: 'phone',
      render: (v) => (v.licensePlate
        ? <Typography variant="body2">{v.licensePlate}{v.plateState ? ` · ${v.plateState}` : ''}</Typography>
        : <Typography variant="caption" color="text.disabled">—</Typography>),
    },
    {
      key: 'vin',
      header: 'VIN',
      hide: 'tablet',
      render: (v) => (v.vin
        ? (
          <Typography sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5 }}>
            {v.vin}
          </Typography>
        )
        // A missing VIN is worth noticing: it is required on the
        // registration and the insurance card, and it is the field most
        // often skipped when a truck is added in a hurry.
        : <Typography variant="caption" sx={{ color: 'warning.dark' }}>Not recorded</Typography>),
    },
    {
      key: 'registration',
      header: 'Registration',
      hide: 'tablet',
      render: (v) => {
        if (!v.registrationExpiresOn) return <Typography variant="caption" color="text.disabled">—</Typography>;
        const expires = new Date(v.registrationExpiresOn);
        const days = Math.floor((expires.getTime() - Date.now()) / 86400000);
        const label = expires.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
        // Red once lapsed, amber inside 30 days — the vault's own thresholds.
        const color = days < 0 ? 'error.main' : days <= 30 ? 'warning.dark' : 'text.primary';
        return <Typography variant="body2" sx={{ color }}>{label}</Typography>;
      },
    },
    {
      key: 'area',
      header: 'Area',
      hide: 'phone',
      render: (v) => (v.serviceAreaId != null
        ? <Typography variant="body2">{v.serviceAreaId}</Typography>
        : <Typography variant="caption" color="text.disabled">—</Typography>),
    },
    {
      key: 'status',
      header: 'Status',
      // Dropped before Paperwork on a phone: the panel's filter already
      // says which group you are looking at, so this column reads "Active"
      // on nearly every row, while Paperwork is the reason to open the
      // fleet from a phone at all.
      hide: 'phone',
      render: (v) => (
        v.status === 'retired'
          ? <ToneChip tone="neutral" label="Retired" />
          : v.status === 'maintenance'
            ? <ToneChip tone="warn" label="In maintenance" />
            : <ToneChip tone="ok" label="Active" />
      ),
    },
    {
      key: 'paperwork',
      header: 'Paperwork',
      render: (v) => {
        const row = compliance[v._id];
        // No obligations reconciled yet is NOT compliant — the vault's
        // rule, and it has to hold here too or the two screens disagree
        // about the same truck.
        if (!row || !row.score || row.score.total === 0) {
          return <Typography variant="caption" color="text.disabled">Not tracked</Typography>;
        }
        if (!row.worstStatus || row.worstStatus === 'satisfied') {
          return <Typography variant="caption" sx={{ color: 'success.dark' }}>All on file</Typography>;
        }
        return (
          <ToneChip
            tone={toneForObligation(row.worstStatus) === 'soon' ? 'warn' : 'danger'}
            label={labelForObligation(row.worstStatus)}
            hollow={row.worstStatus === 'missing'}
          />
        );
      },
    },
    {
      key: 'actions',
      header: '',
      width: 48,
      align: 'right',
      // Editing, retiring and putting a truck back on the road are desk
      // actions. On a phone the row itself still opens that truck's file,
      // which is what somebody is there to see, and dropping this gives
      // the Paperwork column enough room for its own header.
      hide: 'phone',
      render: (v) => (
        <Box
          component="button"
          type="button"
          aria-label={`Actions for ${v.unitNumber}`}
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setMenuVehicle(v);
            setMenuAnchor(event.currentTarget);
          }}
          sx={{
            border: 0, background: 'none', cursor: 'pointer', p: 0.5, borderRadius: 1,
            color: 'text.secondary', display: 'flex', '&:hover': { bgcolor: 'grey.100' },
          }}
        >
          <MoreIcon fontSize="small" />
        </Box>
      ),
    },
  ];

  return (
    <>
      <FleetPanel
        counts={counts}
        filter={filter}
        onFilter={setFilter}
        onAdd={() => { setEditing(null); setDialogOpen(true); }}
      />

      <PageBody>
        <PageHeader
          title="Fleet"
          subtitle="Every vehicle the company runs, and whether it is on the road."
        >
          <TextField
            placeholder="Find a truck by unit, VIN, plate or model…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ maxWidth: 420, width: '100%' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
        </PageHeader>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <DataTable
          twoLine
          columns={columns}
          rows={rows}
          rowKey={(v) => v._id}
          loading={loading}
          onRowClick={(v) => navigate(`/fleet/${v._id}`)}
          rowAccent={(v) => {
            const worst = compliance[v._id]?.worstStatus;
            if (v.status === 'retired' || !worst || worst === 'satisfied') return undefined;
            return worst === 'due_soon' ? statusTokens.warn : statusTokens.danger;
          }}
          empty={(
            <EmptyState
              icon={<TruckIcon />}
              title={search ? 'No truck matches that' : filter === 'retired' ? 'Nothing retired' : 'No vehicles yet'}
              description={search
                ? 'Try the unit number, or part of the VIN.'
                : 'Add the trucks you run and TopoSDS starts tracking their registration, inspection and insurance.'}
              action={!search && filter !== 'retired'
                ? {
                  label: 'Add the first vehicle',
                  icon: <AddIcon />,
                  onClick: () => { setEditing(null); setDialogOpen(true); },
                }
                : undefined}
            />
          )}
        />
      </PageBody>

      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        onClick={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => menuVehicle && navigate(`/fleet/${menuVehicle._id}`)}>View details</MenuItem>
        <MenuItem onClick={() => { setEditing(menuVehicle); setDialogOpen(true); }}>Edit details</MenuItem>
        <MenuItem onClick={() => menuVehicle && navigate(`/files/entity/vehicle/${menuVehicle._id}`)}>
          Open its file
        </MenuItem>
        {menuVehicle?.status !== 'retired' && menuVehicle?.status !== 'maintenance' && (
          <MenuItem onClick={() => menuVehicle && act(() => VehicleService.update(menuVehicle._id, { status: 'maintenance' }))}>
            Mark in maintenance
          </MenuItem>
        )}
        {menuVehicle?.status === 'maintenance' && (
          <MenuItem onClick={() => menuVehicle && act(() => VehicleService.update(menuVehicle._id, { status: 'active' }))}>
            Back on the road
          </MenuItem>
        )}
        {canRetire && menuVehicle?.status !== 'retired' && (
          <MenuItem sx={{ color: 'error.main' }} onClick={() => setRetiring(menuVehicle)}>
            Retire
          </MenuItem>
        )}
        {canRetire && menuVehicle?.status === 'retired' && (
          <MenuItem onClick={() => menuVehicle && act(() => VehicleService.reactivate(menuVehicle._id))}>
            Bring back into service
          </MenuItem>
        )}
      </Menu>

      <VehicleDialog
        open={dialogOpen}
        vehicle={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={() => { setDialogOpen(false); load(); }}
        onImport={canImport ? () => { setDialogOpen(false); setImportOpen(true); } : undefined}
      />

      <ImportVehiclesDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={load}
      />

      <Dialog open={!!retiring} onClose={() => setRetiring(null)}>
        <DialogTitle>Retire {retiring?.unitNumber}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            It stops being tracked for compliance and moves to Retired. Its documents are kept —
            a claim can land years after a van leaves the fleet — and you can bring it back at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRetiring(null)} disabled={busy}>Cancel</Button>
          <Button color="error" disabled={busy} onClick={() => retiring && act(() => VehicleService.retire(retiring._id))}>
            {busy ? 'Retiring…' : 'Retire'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FleetList;
